import React from "react";
import { Button, buttonVariants } from "../ui/button";
import { Video, Copy, QrCode } from "lucide-react";
import PaymentConfirmation from "./PaymentConfirmation";

interface TeleConsultationProps {
  data: any;
  paymentDetailsData: any;
  copied: boolean;
  setCopied: (v: boolean) => void;
  handleViewPrescription: (file: string) => void;

  // Payment confirmation UI
  paymentChecked: boolean;
  setPaymentChecked: (v: boolean) => void;
  paymentRef: string;
  setPaymentRef: (v: string) => void;
  loadingPaymentSubmit: boolean;
  onPaymentSubmit: () => void;
}

const TeleConsultationSection: React.FC<TeleConsultationProps> = ({
  data,
  paymentDetailsData,
  copied,
  setCopied,
  handleViewPrescription,

  paymentChecked,
  setPaymentChecked,
  paymentRef,
  setPaymentRef,
  loadingPaymentSubmit,
  onPaymentSubmit,
}) => {
  const status = data?.status || "";

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <h3 className="text-base font-bold mb-1">Teleconsultation</h3>

      {/* =======================
          STATUS: BOOKED
      ======================== */}
      {status === "BOOKED" && (
        <>
          <p className="text-sm font-bold text-primary mb-3">
            Status: Awaiting confirmation from doctor.
          </p>

          <p className="text-sm text-primary mb-2">
            You will receive Doctor's payment link upon confirmation.
          </p>

          <p className="text-sm text-primary italic mb-2">
            Note: Payment should be done directly to doctor before consultation.
          </p>
        </>
      )}

      {/* =======================
          STATUS: ACCEPTED
      ======================== */}
      {status === "ACCEPTED" && (
        <>
          <p className="text-sm text-primary mb-2">
            Please complete the payment before consultation.
          </p>

          {/* --- UPI ID --- */}
          {paymentDetailsData?.upiId && (
            <div className="bg-gray-50 p-3 rounded-lg flex items-center gap-2">
              <span className="text-sm font-medium">UPI ID:</span>
              <span className="font-mono">{paymentDetailsData.upiId}</span>

              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 sm:h-6 sm:w-6 p-0 flex-shrink-0 relative group hover:bg-gray-200"
                onClick={async () => {
                  await navigator.clipboard.writeText(paymentDetailsData.upiId);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                <Copy className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                <div
                  className={`absolute -top-8 left-1/2 transform -translate-x-1/2
                    bg-gray-900 text-white text-xs px-2 py-1 rounded pointer-events-none
                    transition-opacity whitespace-nowrap z-10
                    ${copied ? "opacity-100" : "opacity-0 group-hover:opacity-100"}
                  `}
                >
                  {copied ? "Copied!" : "Copy"}
                </div>
              </Button>
            </div>
          )}

          {/* --- QR Code --- */}
          {paymentDetailsData?.base64File && (
            <div className="flex justify-center px-4 sm:px-0 mt-4">
              <div
                className="group flex cursor-pointer flex-col items-center rounded-xl
                border-2 border-dashed border-blue-200 p-4 sm:p-6 shadow-sm
                hover:border-blue-300 hover:shadow-md transition-all bg-gradient-to-b
                from-blue-50 to-white w-full max-w-[160px] sm:max-w-[200px]"
                onClick={() => handleViewPrescription(paymentDetailsData.base64File)}
              >
                <div className="p-2 sm:p-3 bg-blue-100 rounded-full mb-2 sm:mb-3 group-hover:bg-blue-200">
                  <QrCode className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                </div>

                <p className="font-semibold text-gray-900 text-center mb-1 text-sm sm:text-base">
                  Scan QR Code
                </p>
                <p className="text-xs text-gray-500 text-center">Tap to view full size</p>
              </div>
            </div>
          )}

          {/* --- Payment Confirmation Section --- */}
          {(paymentDetailsData?.upiId || paymentDetailsData?.base64File) && (
            <div className="mt-5 border-t pt-4">
              <h4 className="text-sm font-semibold mb-1">
                Payment confirmation
              </h4>

              <PaymentConfirmation
                checked={paymentChecked}
                onCheckedChange={setPaymentChecked}
                paymentInfo={paymentRef}
                onPaymentInfoChange={setPaymentRef}
                loading={loadingPaymentSubmit}
                onSubmit={onPaymentSubmit}
              />
            </div>
          )}
        </>
      )}

      {/* =======================
          STATUS: CONFIRMED
      ======================== */}
      {status === "CONFIRMED" && (
        <>
          <p className="text-sm text-primary mb-3">Your teleconsultation is confirmed.</p>

          <div className="flex justify-end">
            <Button
              onClick={() =>
                window.open(
                  `https://developer-videoconf-1843.app.100ms.live/meeting/${data?.patientCode}`,
                  "_blank"
                )
              }
              className={buttonVariants({
                className: "h-10 px-5 text-sm font-medium",
              })}
            >
              <Video className="w-4 h-4 mr-2" />
              Join Teleconsultation
            </Button>
          </div>

          <p className="text-sm text-primary italic mt-3">
            Note: Please join teleconsultation at the scheduled time.
          </p>
        </>
      )}
    </div>
  );
};

export default TeleConsultationSection;
